import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import { loginRequest, ACCEPTED_DOMAIN, isMsalConfigured } from "../msalConfig";
import { practices } from "../data/content";
import { scrollToSection } from "../utils/scroll";
import { trackPage } from "../utils/track";

const NAV_SECTIONS = ["home", "about", "services", "leaders", "contact"];

function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();

  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [error, setError] = useState("");
  const [signingIn, setSigningIn] = useState(false);
  const headerRef = useRef(null);

  const onHome = location.pathname === "/";

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, []);

  // Close the mobile menu whenever the route changes.
  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.classList.toggle("nav-open", menuOpen);
    return () => document.body.classList.remove("nav-open");
  }, [menuOpen]);

  // Scroll spy — only meaningful on the home page.
  useEffect(() => {
    if (!onHome) return undefined;

    const onScroll = () => {
      const probe = window.scrollY + 200;
      let current = "home";
      NAV_SECTIONS.forEach((id) => {
        const el = document.getElementById(id);
        if (el && probe >= el.offsetTop && probe <= el.offsetTop + el.offsetHeight) {
          current = id;
        }
      });
      setActiveSection(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onHome]);

  // Close the desktop dropdown on an outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const goToSection = (id) => {
    closeMenu();
    setActiveSection(id);
    // Sections are anchors on the home page, so the URL is "/#id".
    trackPage(id === "home" ? "/" : `/#${id}`);
    if (onHome) {
      scrollToSection(id);
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const goToService = (hash) => {
    closeMenu();
    const path = `/Services/Services#${hash}`;
    trackPage(path, practices.find((p) => p.id === hash)?.title);
    navigate(path);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    closeMenu();

    if (!isMsalConfigured) {
      setError(
        "Sign-in is not configured for this environment. Set REACT_APP_MSAL_CLIENT_ID and REACT_APP_MSAL_AUTHORITY."
      );
      return;
    }

    setSigningIn(true);

    try {
      const response = await instance.loginPopup({
        ...loginRequest,
        prompt: "login",
      });

      const account = response.account;
      const userEmail = (account?.username || "").toLowerCase();
      const userDomain = userEmail.split("@")[1];

      if (userDomain !== ACCEPTED_DOMAIN) {
        setError("Access denied: Please use a company account.");
        await instance.logoutPopup();
        return;
      }

      instance.setActiveAccount(account);
      navigate("/login");
    } catch (err) {
      const silentErrors = [
        "user_cancelled",
        "popup_window_error",
        "user closed the popup window",
        "interaction_in_progress",
      ];
      const errStr = err?.errorCode || err?.errorMessage || err?.message || "";

      if (silentErrors.some((code) => errStr.toLowerCase().includes(code))) {
        // The visitor closed or cancelled the popup — not an error worth showing.
        return;
      }
      // eslint-disable-next-line no-console
      console.error("Login error:", err);
      setError("Login failed. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  const linkClass = (id) => (onHome && activeSection === id ? "here" : undefined);

  return (
    <header ref={headerRef}>
      <div className="topnav">
        <div className="wrap">
          <button
            type="button"
            className="brand-logo"
            onClick={() => goToSection("home")}
            aria-label="New Age Services — go to home"
          />

          <button
            type="button"
            className="navtoggle"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span />
          </button>

          <nav className="navlinks" aria-label="Primary">
            <button type="button" className={linkClass("home")} onClick={() => goToSection("home")}>
              Home
            </button>
            <button type="button" className={linkClass("about")} onClick={() => goToSection("about")}>
              About
            </button>

            <div className={`dd${dropdownOpen ? " open" : ""}`}>
              <button
                type="button"
                className={linkClass("services")}
                aria-expanded={dropdownOpen}
                onClick={() => {
                  if (window.innerWidth < 1000) {
                    setDropdownOpen((v) => !v);
                  } else {
                    goToSection("services");
                  }
                }}
              >
                Services ▾
              </button>
              <div className="dd-menu">
                {practices.map((p) => (
                  <button key={p.id} type="button" onClick={() => goToService(p.id)}>
                    {p.title}
                  </button>
                ))}
              </div>
            </div>

            <button type="button" className={linkClass("leaders")} onClick={() => goToSection("leaders")}>
              Leaders
            </button>
            <button
              type="button"
              className={location.pathname.startsWith("/JobAvailability") ? "here" : undefined}
              onClick={() => {
                closeMenu();
                trackPage("/JobAvailability/JobAvailability");
                navigate("/JobAvailability/JobAvailability");
              }}
            >
              Careers
            </button>
            <button type="button" className={linkClass("contact")} onClick={() => goToSection("contact")}>
              Contact
            </button>

            <button type="button" className="btn sm" onClick={handleLogin} disabled={signingIn}>
              {signingIn ? "Signing in…" : "Client sign in"}
            </button>
          </nav>
        </div>
      </div>

      {error && (
        <div className="login-error" role="alert">
          <div className="wrap">{error}</div>
        </div>
      )}
    </header>
  );
}

export default Header;
